/**
 * Situação do membro nas escalas: **ativo** pode ser escalado (com ou sem acesso ao
 * Escala — o líder também escala quem nunca entrou); **inativo** sai das escalas de hoje
 * em diante e dos ministérios (funções habilitadas, lideranças, treinamento e pares), fica
 * fora da geração, do seletor, da distribuição e das contagens, e não entra mais no Escala.
 * O histórico passado continua. Reativar devolve a pessoa às listas e ao login; os
 * ministérios e as escalas de que saiu não voltam sozinhos. A disponibilidade fica.
 */
import type { DomainState, Member, Ministry, Role, ScheduleEvent } from './types';

export function isActiveMember(member: Pick<Member, 'inactiveSince'>): boolean {
  return member.inactiveSince === null;
}

export function activeMembers<T extends Pick<Member, 'inactiveSince'>>(members: readonly T[]): T[] {
  return members.filter(isActiveMember);
}

/** Ainda vai acontecer para a escala: de hoje em diante e, hoje, sem check-in. */
export function stillAhead(event: ScheduleEvent, today: string): boolean {
  return event.date > today || (event.date === today && Object.keys(event.attendance).length === 0);
}

export interface DeactivationImpactEvent {
  event: ScheduleEvent;
  /** Funções em que a pessoa está escalada neste evento. */
  roles: Role[];
}

export interface DeactivationImpact {
  /** Escalas de hoje em diante de que a pessoa sai, em ordem de data. */
  events: DeactivationImpactEvent[];
  /** Quantas dessas já estavam publicadas (a vaga reabre e o líder precisa preencher). */
  published: number;
  /** Ministérios de que sai, com as funções em que estava habilitada. */
  ministries: { ministry: Ministry; roles: Role[] }[];
  /** Lideranças desfeitas: o ministério (`role` nulo) ou a função fica sem líder. */
  leads: { ministry: Ministry; role: Role | null }[];
}

/** Por que não dá para inativar (o próprio cadastro); `null` quando dá. */
export function deactivationError(memberId: string, actorId: string): string | null {
  return memberId === actorId ? 'Você não pode inativar o próprio cadastro.' : null;
}

/** O que inativar muda — o diálogo mostra antes de confirmar. */
export function deactivationImpact(
  state: Pick<DomainState, 'events' | 'ministries' | 'members'>,
  memberId: string,
  today: string
): DeactivationImpact {
  const enabled = state.members.find((m) => m.id === memberId)?.roles ?? {};
  const ministries = state.ministries.flatMap((ministry) => {
    const roles = ministry.roles.filter((r) => (enabled[ministry.id] ?? []).includes(r.id));

    return roles.length ? [{ ministry, roles }] : [];
  });
  const roleById = new Map(state.ministries.flatMap((m) => m.roles.map((r) => [r.id, r] as const)));
  const events = state.events
    .filter((e) => stillAhead(e, today))
    .flatMap((event): DeactivationImpactEvent[] => {
      const roles = Object.entries(event.slots)
        .filter(([, ids]) => ids.includes(memberId))
        .flatMap(([roleId]) => roleById.get(roleId) ?? []);

      return roles.length ? [{ event, roles }] : [];
    })
    .sort((a, b) => a.event.date.localeCompare(b.event.date) || a.event.time.localeCompare(b.event.time));
  const leads = state.ministries.flatMap((ministry) => [
    ...(ministry.leaderIds.includes(memberId) ? [{ ministry, role: null }] : []),
    ...ministry.roles.filter((r) => r.leaderIds.includes(memberId)).map((role) => ({ ministry, role })),
  ]);

  return { events, published: events.filter((e) => e.event.published).length, ministries, leads };
}

/**
 * Inativa: marca a data, tira a pessoa dos ministérios (funções habilitadas, lideranças,
 * treinamento e pares restritos) e das escalas de hoje em diante (as respostas e recusas
 * dela saem junto). A escala publicada continua publicada, com a vaga aberta — quem já
 * recebeu o aviso não é surpreendido por um rascunho.
 */
export function deactivateMember<S extends Pick<DomainState, 'members' | 'events' | 'ministries'>>(
  state: S,
  memberId: string,
  today: string
): S {
  const target = state.members.find((m) => m.id === memberId);

  if (!target || !isActiveMember(target)) return state;

  return {
    ...state,
    members: state.members.map((m) => (m.id === memberId ? { ...m, inactiveSince: today, roles: {} } : m)),
    ministries: state.ministries.map((ministry) => ({
      ...ministry,
      leaderIds: ministry.leaderIds.filter((id) => id !== memberId),
      roles: ministry.roles.map((role) => ({
        ...role,
        leaderIds: role.leaderIds.filter((id) => id !== memberId),
        trainees: role.trainees.filter((id) => id !== memberId),
        blockedPairs: role.blockedPairs.filter((pair) => !pair.includes(memberId)),
        togetherPairs: role.togetherPairs.filter((pair) => !pair.includes(memberId)),
      })),
    })),
    events: state.events.map((event) => {
      const involved =
        Object.values(event.slots).some((ids) => ids.includes(memberId)) || memberId in event.responses;

      if (!involved || !stillAhead(event, today)) return event;

      const { [memberId]: _response, ...responses } = event.responses;

      return {
        ...event,
        slots: Object.fromEntries(
          Object.entries(event.slots).map(([roleId, ids]) => [roleId, ids.filter((id) => id !== memberId)])
        ),
        responses,
      };
    }),
  };
}

/** Reativa: volta às listas e ao login; ministérios e escalas de que saiu não voltam sozinhos. */
export function reactivateMember(members: readonly Member[], memberId: string): Member[] {
  return members.map((m) => (m.id === memberId ? { ...m, inactiveSince: null } : m));
}
